// Pure display helpers for the KB effectiveness page (no React).

import type { Finding, FindingKind, ProfileScorecard } from '../../services/kbEffectiveness';

export function pct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–';
  return `${Math.round(value * 100)}%`;
}

export function ratio(num: number, den: number): number | null {
  return den > 0 ? num / den : null;
}

export function whenUtc(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
}

/** Which finding kinds, per profile, the stored report flagged — the page
 *  colours a cell from THIS, never from its own threshold arithmetic. */
export function flaggedKinds(findings: Finding[]): Map<string, Set<FindingKind>> {
  const out = new Map<string, Set<FindingKind>>();
  for (const f of findings) {
    if (!f.profile_id) continue;
    const set = out.get(f.profile_id) ?? new Set<FindingKind>();
    set.add(f.kind);
    out.set(f.profile_id, set);
  }
  return out;
}

/** The headline cells of one scorecard row, with the finding kind that would mark each one. */
export interface HeadlineCell {
  key: string;
  label: string;
  value: string;
  detail: string;
  kinds: FindingKind[];
}

export function headlineCells(card: ProfileScorecard): HeadlineCell[] {
  const r = card.record;
  const d = card.read;
  const c = card.corrections;
  return [
    {
      key: 'write_error_rate',
      label: 'Write errors',
      value: r.writes ? pct(r.error_rate) : '–',
      detail: `${r.error}/${r.writes} writes errored, ${r.unparsed} malformed`,
      kinds: ['write_error_rate'],
    },
    {
      key: 'misrouted',
      label: 'Misrouted writes',
      value: String(r.misrouted_writes),
      detail: 'writes aimed at a bank the profile holds read-only or does not mount',
      kinds: ['misrouted_writes_live', 'misrouted_writes_historic'],
    },
    {
      key: 'kb_before_sql',
      label: 'KB before SQL',
      value: d.turns_with_sql ? pct(d.kb_before_sql_rate) : '–',
      detail: `${d.turns_kb_before_sql}/${d.turns_with_sql} data-query turns read a bank first`,
      kinds: ['kb_before_sql'],
    },
    {
      key: 'playbook_list_first',
      label: 'Playbook list first',
      value: d.turns_with_sql ? pct(d.playbook_list_before_sql_rate) : '–',
      detail: `${d.turns_playbook_list_before_sql}/${d.turns_with_sql} data-query turns listed the own playbook first`,
      kinds: ['playbook_list_before_sql'],
    },
    {
      key: 'near_dup',
      label: 'Near-dups consolidated',
      value: pct(ratio(r.near_dup_followed_by_edit, r.near_dup_warned)),
      detail: `${r.near_dup_followed_by_edit}/${r.near_dup_warned} near-duplicate warnings followed by an edit`,
      kinds: ['near_dup_unconsolidated'],
    },
    {
      key: 'corrections',
      label: 'Corrections recorded',
      value: c && c.corrections ? pct(c.recorded_rate) : '–',
      detail: c ? `${c.recorded}/${c.corrections} human corrections followed by a KB write` : 'not an interactive surface',
      kinds: ['corrections_unrecorded'],
    },
  ];
}
